import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getMergedCompetencies } from 'lib/competencies';
import { ContentLabel, ModalButtonWithForm } from 'components/common';
import { Select } from 'components/common/form';
import { ProfileCompetenciesCompareResult } from 'components/profile';

import type { RequestUser, UserCompetency } from 'shared-types';
import type { GetCompetencyProfilesRes } from 'shared-types/responses';

interface Props {
  buttonIsDropdown?: boolean;
  competencyProfiles: GetCompetencyProfilesRes['competencyProfiles'];
  targetUser: RequestUser;
  targetUserCompetencies: UserCompetency[];
}

const ProfileCompetenciesCompare = ({
  buttonIsDropdown = true,
  competencyProfiles,
  targetUser,
  targetUserCompetencies
}: Props) => {
  const { language, messages, theme } = useReduxState();

  const mergedCompetencies = getMergedCompetencies(targetUserCompetencies);

  const getNumberOfMatchingUserCompetencies = (
    competencies: GetCompetencyProfilesRes['competencyProfiles'][0]['competencies']
  ) => {
    const numberOfMatchingCompetencies =
      competencies?.filter((competency) =>
        mergedCompetencies.some(
          (userCompetency) =>
            competency.id === userCompetency.id &&
            ((!competency.level?.id && !userCompetency.level?.id) ||
              userCompetency.highestLevel?.level >= competency.level?.level)
        )
      )?.length || 0;

    return numberOfMatchingCompetencies;
  };

  return (
    <ModalButtonWithForm
      buttonCategory="lighter"
      buttonIcon="address-card"
      buttonIsDropdown={buttonIsDropdown}
      buttonSize="header"
      buttonText={messages.competencyProfilesCompare}
      confirmChanges={false}
      contentStyle={{ padding: 0 }}
      footerCloseButton={true}
      modalCloseButton={true}
      modalTitle={messages.competencyProfilesCompare}
      modalWidth="medium"
    >
      {({ values }) => {
        const selectedProfile = competencyProfiles.find(
          (profile) => profile.id === values?.competencyProfileId
        );

        return (
          <div className="content">
            <div className="header">
              <ContentLabel
                horizontal={true}
                horizontalBreakpoint={breakpoints.small}
                label={messages.competencyProfile}
              >
                <div className="select">
                  <Select
                    field="competencyProfileId"
                    isClearable={true}
                    isSearchable={true}
                    options={[
                      ...(competencyProfiles.map((profile) => ({
                        label:
                          profile.titleLocales?.[language] || profile.title,
                        value: profile.id
                      })) || [])
                    ]}
                    style={{ width: '100%' }}
                  />
                </div>
              </ContentLabel>
            </div>

            <div className="results">
              {values?.competencyProfileId ? (
                <ProfileCompetenciesCompareResult
                  numberOfMatchedCompetencies={getNumberOfMatchingUserCompetencies(
                    selectedProfile.competencies
                  )}
                  numberOfTotalCompetencies={
                    selectedProfile.competencies.length
                  }
                  profileCompetencies={selectedProfile.competencies}
                  user={targetUser}
                  userCompetencies={mergedCompetencies}
                />
              ) : (
                <div className="not-selected">
                  {messages.competencyProfilesSelect}
                </div>
              )}
            </div>

            <style jsx>{`
              .header {
                border-bottom: 1px solid ${theme.lightGreyDark};
                padding: 15px;
              }
              .select {
                width: 300px;
              }
              .results {
                padding: 15px;
              }
              .not-selected {
                align-items: center;
                color: ${theme.textColorLight};
                display: flex;
                font-size: 1.5rem;
                font-weight: 600;
                justify-content: center;
                padding: 30px 20px;
              }
            `}</style>
          </div>
        );
      }}
    </ModalButtonWithForm>
  );
};

export default ProfileCompetenciesCompare;
